import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowLeft } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";

const CURL_LIST = `curl -H "X-API-Key: loomi_sk_XXXXXXXXXXXXXXXXXXXXXXXX" \\
  ${'PUBLIC_BASE'}/api/v1/projects`;

const CURL_PING = `curl -H "X-API-Key: loomi_sk_XXXXXXXXXXXXXXXXXXXXXXXX" \\
  ${'PUBLIC_BASE'}/api/v1/ping`;

const CURL_STATS = `curl -H "X-API-Key: loomi_sk_XXXXXXXXXXXXXXXXXXXXXXXX" \\
  ${'PUBLIC_BASE'}/api/v1/stats`;

const PAYLOAD = `{
  "event": "session.exit",
  "session_id": "8b7d2c…",
  "project_id": "ecom_q1_2026",
  "vendor_id": "vnd_A1B2C3",
  "vendor_sub_id": "your-internal-panelist-id",
  "rid": "abc123",
  "status": "complete",
  "timestamp": "2026-02-26T12:34:56+00:00"
}`;

const VERIFY_PY = `import hmac, hashlib

body = request.body  # raw bytes
sig  = request.headers["X-SyncLine-Signature"]  # e.g. "sha256=abc..."
expected = "sha256=" + hmac.new(WEBHOOK_SECRET.encode(), body, hashlib.sha256).hexdigest()

if not hmac.compare_digest(sig, expected):
    return 401  # tampered or wrong secret`;

const ENTRY_URL = "{PUBLIC_BASE}/api/survey/entry?pid={PROJECT_ID}&rid={YOUR_UNIQUE_RID}&vid={YOUR_VENDOR_ID}&sub_id={YOUR_PANELIST_ID}";

export default function VendorDocs() {
  const base = BACKEND_URL || "https://syncline.example.com";
  const renderCurl = (t) => t.replace(/PUBLIC_BASE/g, base);

  return (
    <div className="min-h-screen bg-[#050505] text-white relative overflow-hidden" data-testid="vendor-docs-page">
      <div className="aurora" />
      <nav className="relative z-10 glass border-b border-white/10 sticky top-0">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="docs-nav-logo">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </div>
            <span className="font-display font-extrabold text-xl">SyncLine</span>
            <span className="text-xs uppercase tracking-widest text-mute ml-3">Vendor API</span>
          </Link>
          <Link to="/" className="text-xs text-mute hover:text-white inline-flex items-center gap-1" data-testid="docs-nav-back"><ArrowLeft className="w-3 h-3" /> back to site</Link>
        </div>
      </nav>

      <main className="relative z-10 max-w-4xl mx-auto px-6 py-16">
        <span className="inline-block px-3 py-1 rounded-full border border-indigo-500/40 bg-indigo-500/10 text-xs text-indigo-200 uppercase tracking-widest mb-6" data-testid="docs-eyebrow">Supply-side integration · v1</span>
        <h1 className="font-display font-extrabold text-5xl md:text-6xl leading-[0.95] mb-4">Vendor API integration</h1>
        <p className="text-lg text-mute max-w-2xl leading-relaxed mb-12">
          Integrate your panel with SyncLine to receive project opportunities, route respondents in, and get real-time postback callbacks — the same pattern Cint, Dynata, and Zamplia use with their supply partners.
        </p>

        <Doc title="1 · Authentication" testId="doc-auth">
          <p>Every request to the vendor API is authenticated with a per-vendor API key, issued by a SyncLine admin. Send it on every request in the <code>X-API-Key</code> header.</p>
          <pre className="code" data-testid="doc-auth-curl">{renderCurl(CURL_PING)}</pre>
          <ul className="list-disc pl-5 text-sm text-mute space-y-1">
            <li>Keys are prefixed with <code>loomi_sk_</code> and shown only once at creation. Rotate at any time from the admin console.</li>
            <li>Invalid or inactive keys return <code>401</code>. Never embed keys in a browser bundle.</li>
          </ul>
        </Doc>

        <Doc title="2 · Discover live projects" testId="doc-projects">
          <p>Pull the live project catalog. Each project includes CPI, LOI, incidence rate, target N, quotas, and the entry URL template.</p>
          <pre className="code">{renderCurl(CURL_LIST)}</pre>
          <p className="text-sm text-mute">Response shape (abbreviated):</p>
          <pre className="code text-[11px]">{`{
  "vendor_id": "vnd_A1B2C3",
  "count": 3,
  "projects": [
    {
      "id": "acme_q1",
      "name": "Consumer Habits Q1",
      "cpi": 2.5,
      "loi_minutes": 12,
      "incidence_rate_pct": 45,
      "target_n": 500,
      "quotas": { "country": { "US": 300, "IN": 200 } },
      "entry_url_template": "${base}/api/survey/entry?pid=acme_q1&rid=[YOUR_RID]&vid=[VENDOR_ID]&sub_id=[YOUR_PANELIST_ID]"
    }
  ]
}`}</pre>
        </Doc>

        <Doc title="3 · Route a respondent in" testId="doc-entry">
          <p>Redirect your panelist&apos;s browser to the entry URL, populating the placeholders with your values:</p>
          <pre className="code font-mono">{ENTRY_URL.replace("{PUBLIC_BASE}", base)}</pre>
          <ul className="list-disc pl-5 text-sm text-mute space-y-1">
            <li><code>pid</code> — the project ID from the feed.</li>
            <li><code>rid</code> — a <b>unique</b> respondent identifier per attempt (used for idempotency).</li>
            <li><code>vid</code> — your vendor ID (from onboarding).</li>
            <li><code>sub_id</code> — your internal panelist ID (round-trips back to you in the postback).</li>
          </ul>
          <p className="text-sm text-mute">SyncLine will redirect to the client&apos;s survey URL and, upon exit, land the respondent on a signed status page.</p>
        </Doc>

        <Doc title="4 · Receive postback callbacks" testId="doc-postback">
          <p>Set a <code>postback_url</code> on your vendor record. SyncLine sends a signed JSON POST as soon as an exit event is recorded (complete, terminate, overquota, quality_term, security_term). Delivery is fire-and-forget with a 6s timeout.</p>
          <pre className="code" data-testid="doc-postback-payload">{PAYLOAD}</pre>
          <p className="text-sm text-mute">Headers:</p>
          <pre className="code text-[11px]">{`X-SyncLine-Signature: sha256=<hex>
X-SyncLine-Event: session.exit
Content-Type: application/json
User-Agent: loomi-webhook/1.0`}</pre>
          <p className="text-sm text-mute">Verify the signature with your <code>webhook_secret</code>:</p>
          <pre className="code" data-testid="doc-verify-code">{VERIFY_PY}</pre>
        </Doc>

        <Doc title="5 · Reconcile & report" testId="doc-reconcile">
          <p>Pull attributed sessions and revenue stats at any time.</p>
          <pre className="code">{renderCurl(CURL_STATS)}</pre>
          <pre className="code text-[11px]">{`GET /api/v1/reconciliation?status=complete&project_id=acme_q1
GET /api/v1/reconciliation?status=terminate`}</pre>
        </Doc>

        <Doc title="6 · Reference · status codes" testId="doc-status">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-widest text-mute border-b border-white/10">
              <th className="py-2 pr-4">Status</th><th className="py-2">Meaning</th>
            </tr></thead>
            <tbody>
              <Row s="complete" v="Respondent successfully finished the survey (billable)." />
              <Row s="terminate" v="Screened out — did not qualify (not billable)." />
              <Row s="overquota" v="Quota already full at the time of exit." />
              <Row s="quality_term" v="Flagged by client&apos;s quality checks (straightliners, speeders, gibberish)." />
              <Row s="security_term" v="Fraud/bot signal — do not resend the same respondent." />
            </tbody>
          </table>
        </Doc>

        <p className="text-xs text-mute mt-16">Questions? Email <a href="mailto:vendor-integrations@synclineresearch.com" className="text-indigo-300 hover:underline">vendor-integrations@synclineresearch.com</a>.</p>
      </main>
      <style>{`.code{background:rgba(0,0,0,0.6);border:1px solid rgba(255,255,255,0.1);border-radius:10px;padding:14px 16px;margin:12px 0;overflow-x:auto;white-space:pre;font-family:'JetBrains Mono',monospace;font-size:12px;color:#e5e7eb;line-height:1.6}`}</style>
    </div>
  );
}
function Doc({ title, testId, children }) {
  return (
    <section className="mb-14" data-testid={testId}>
      <h2 className="font-display font-extrabold text-2xl md:text-3xl mb-4">{title}</h2>
      <div className="space-y-2 text-sm leading-relaxed">{children}</div>
    </section>
  );
}
function Row({ s, v }) {
  return (
    <tr className="border-b border-white/5">
      <td className="py-2 pr-4 font-mono text-xs">{s}</td>
      <td className="py-2 text-mute">{v}</td>
    </tr>
  );
}
